import { useEffect, useMemo, useState } from "react";
import { useQueryCore } from "@/hooks/useQueryCore";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import { getVisits } from "@/lib/api";
import { localCache } from "@/lib/cache";

// Returns ISO date strings for the first and last day of a given year/month.
function monthBounds(year, month) {
  const mm      = String(month).padStart(2, "0");
  const lastDay = new Date(year, month, 0).getDate();
  return {
    date_from: `${year}-${mm}-01`,
    date_to:   `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
  };
}

// Month-scoped Dexie key so each month is cached independently (avoids one
// giant all-time blob growing unboundedly in IndexedDB).
function monthCacheKey(districtId, year, month) {
  return `visits_district_${districtId}_${year}_${String(month).padStart(2, "0")}`;
}

export function useVisitsAgenda(year, month) {
  const { districtId, coordinatorChurchId } = useDistrictGuard();
  const [churchNameMap, setChurchNameMap] = useState({});

  const { date_from, date_to } = monthBounds(year, month);

  const { data, loading, error, refetch } = useQueryCore({
    key:      ["visits-month", districtId, coordinatorChurchId, year, month],
    enabled:  !!districtId,
    cacheKey: districtId ? monthCacheKey(districtId, year, month) : null,
    queryFn:  () => getVisits({
      district_id: coordinatorChurchId ? undefined : districtId,
      church_id:   coordinatorChurchId ?? undefined,
      date_from,
      date_to,
    }),
  });

  useEffect(() => {
    if (!districtId) return;
    localCache.get(`churches_${districtId}`).then((churches) => {
      if (!Array.isArray(churches)) return;
      const map = {};
      churches.forEach((c) => { if (c.id) map[c.id] = c.name; });
      setChurchNameMap(map);
    }).catch(() => {});
  }, [districtId]);

  const allVisits = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  // Data is already month-scoped from the API — no need to re-filter by year/month.
  const visitsByDate = useMemo(() => {
    const map = {};
    allVisits.forEach((v) => {
      if (!v.date) return;
      if (!map[v.date]) map[v.date] = [];
      map[v.date].push({ ...v, churchName: churchNameMap[v.church_id] || null });
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      })
    );
    return map;
  }, [allVisits, churchNameMap]);

  const daysWithVisits = useMemo(
    () => new Set(Object.keys(visitsByDate).map((d) => parseInt(d.split("-")[2], 10))),
    [visitsByDate]
  );

  return { visitsByDate, daysWithVisits, loading, error, refetch, allVisits };
}

// Accepts the allVisits array already fetched by useVisitsAgenda — no second network request.
export function useVisitMetrics(allVisits, loading) {
  const metrics = useMemo(() => {
    const today = new Date();
    const toLocal = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const todayStr     = toLocal(today);
    const weekStart    = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
    const weekEnd      = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() + 6);
    const weekStartStr = toLocal(weekStart);
    const weekEndStr   = toLocal(weekEnd);

    // Data is already month-scoped from the API.
    const pending   = allVisits.filter((v) => v.status === "pendiente").length;
    const completed = allVisits.filter((v) => v.status === "realizada").length;
    const total     = allVisits.length;

    const familyIds = new Set(
      allVisits
        .filter((v) => v.status === "pendiente" && v.target_family_id)
        .map((v) => v.target_family_id)
    );

    const weekVisits = allVisits.filter(
      (v) => v.date && v.date >= weekStartStr && v.date <= weekEndStr
    ).length;

    const completedToday = allVisits.filter(
      (v) => v.status === "realizada" && v.completed_at && v.completed_at.slice(0, 10) === todayStr
    ).length;

    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

    // Families with pending visits in the current month (replaces the previous
    // all-time scan; familiesUnvisited30d was not consumed by any page).
    const familiesUnvisited30d = familyIds.size;

    return {
      total,
      pending,
      completed,
      familiesPending:     familyIds.size,
      weekVisits,
      completedToday,
      completionPct,
      familiesUnvisited30d,
    };
  }, [allVisits]);

  return { metrics, loading };
}
